import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Layout } from '../components/Layout';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../components/ui/table';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../components/ui/dialog';
import { Handshake, Plus, Edit, Trash2, Users, UsersRound, Loader2, ChevronRight } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const PartnersPage = () => {
  const navigate = useNavigate();
  const [partners, setPartners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({
    name: '',
    commission_code: '',
    description: '',
    parent_partner_id: '',
    create_admin: false,
    admin_name: '',
    admin_email: '',
    admin_password: '',
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => { fetchPartners(); }, []);

  const fetchPartners = async () => {
    try {
      const res = await axios.get(`${API_URL}/partners`);
      setPartners(res.data);
    } catch (err) {
      toast.error('Erro ao carregar parceiros');
    } finally {
      setLoading(false);
    }
  };

  const openCreate = (e) => {
    e.stopPropagation();
    setEditing(null);
    setForm({ name: '', commission_code: '', description: '', parent_partner_id: '', create_admin: false, admin_name: '', admin_email: '', admin_password: '' });
    setShowModal(true);
  };

  const openEdit = (e, partner) => {
    e.stopPropagation();
    setEditing(partner);
    setForm({ name: partner.name, commission_code: partner.commission_code || '', description: partner.description || '', parent_partner_id: partner.parent_partner_id || '', create_admin: false, admin_name: '', admin_email: '', admin_password: '' });
    setShowModal(true);
  };

  const handleSave = async () => {
    if (!form.name.trim()) { toast.error('Nome é obrigatório'); return; }
    // Validate admin fields if create_admin is on (only on create)
    if (!editing && form.create_admin) {
      if (!form.admin_name.trim()) { toast.error('Nome do admin é obrigatório'); return; }
      if (!form.admin_email.trim()) { toast.error('Email do admin é obrigatório'); return; }
      if (!form.admin_password || form.admin_password.length < 6) { toast.error('Password do admin precisa de pelo menos 6 caracteres'); return; }
    }
    setSaving(true);
    try {
      if (editing) {
        const partnerData = { name: form.name, commission_code: form.commission_code, description: form.description, parent_partner_id: form.parent_partner_id };
        await axios.put(`${API_URL}/partners/${editing.id}`, partnerData);
        toast.success('Parceiro atualizado');
      } else {
        const partnerData = { name: form.name, commission_code: form.commission_code, description: form.description, parent_partner_id: form.parent_partner_id };
        const partnerRes = await axios.post(`${API_URL}/partners`, partnerData);
        const newPartnerId = partnerRes.data?.id;
        toast.success('Parceiro criado');
        if (form.create_admin && newPartnerId) {
          try {
            await axios.post(`${API_URL}/users`, {
              name: form.admin_name.trim(),
              email: form.admin_email.trim().toLowerCase(),
              password: form.admin_password,
              role: 'admin',
              partner_id: newPartnerId,
            });
            toast.success(`Admin "${form.admin_name}" criado e associado ao parceiro`);
          } catch (uErr) {
            toast.error(`Parceiro OK, mas erro ao criar admin: ${uErr?.response?.data?.detail || 'erro desconhecido'}`);
          }
        }
      }
      setShowModal(false);
      fetchPartners();
    } catch (err) {
      toast.error('Erro ao guardar parceiro');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (e, id) => {
    e.stopPropagation();
    if (!window.confirm('Eliminar parceiro? As equipas e utilizadores associados serão desvinculados.')) return;
    try {
      await axios.delete(`${API_URL}/partners/${id}`);
      toast.success('Parceiro eliminado');
      fetchPartners();
    } catch (err) {
      toast.error('Erro ao eliminar parceiro');
    }
  };

  return (
    <Layout>
      <div className="space-y-6" data-testid="partners-page">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-slate-900 font-outfit flex items-center gap-2" data-testid="partners-title">
              <Handshake className="w-6 h-6 text-[#2D3E6F]" />
              Parceiros
            </h1>
            <p className="text-slate-500 mt-1">{partners.length} parceiros registados</p>
          </div>
          <Button onClick={openCreate} className="bg-[#2D3E6F] hover:bg-[#1a2a4a] rounded-full gap-2" data-testid="create-partner-btn">
            <Plus className="w-4 h-4" /> Novo Parceiro
          </Button>
        </div>

        <Card className="rounded-2xl border-slate-100 overflow-hidden">
          {loading ? (
            <div className="flex items-center justify-center h-48">
              <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
            </div>
          ) : partners.length === 0 ? (
            <div className="text-center py-16">
              <Handshake className="w-12 h-12 text-slate-300 mx-auto mb-4" />
              <p className="text-slate-400">Nenhum parceiro registado</p>
              <Button onClick={openCreate} variant="outline" className="mt-4">
                Criar primeiro parceiro
              </Button>
            </div>
          ) : (
          <div className="overflow-x-auto">
            <Table className="min-w-[700px]">
              <TableHeader>
                <TableRow className="bg-slate-50/50">
                  <TableHead className="font-semibold">Nome</TableHead>
                  <TableHead className="font-semibold">Parceiro Pai</TableHead>
                  <TableHead className="font-semibold">Codigo Comissao</TableHead>
                  <TableHead className="font-semibold">Membros</TableHead>
                  <TableHead className="font-semibold">Equipas</TableHead>
                  <TableHead className="font-semibold">Estado</TableHead>
                  <TableHead className="w-28"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {partners.map(p => (
                  <TableRow
                    key={p.id}
                    className="hover:bg-slate-50/80 cursor-pointer transition-colors"
                    onClick={() => navigate(`/partners/${p.id}`)}
                    data-testid={`partner-row-${p.id}`}
                  >
                    <TableCell>
                      <span className="font-medium text-[#2D3E6F] hover:text-[#4DD4E8]">{p.name}</span>
                    </TableCell>
                    <TableCell>
                      {p.parent_partner_name ? (
                        <span className="text-sm text-slate-600">{p.parent_partner_name}</span>
                      ) : (
                        <span className="text-xs text-slate-400">-</span>
                      )}
                    </TableCell>
                    <TableCell>
                      <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-mono font-semibold bg-amber-50 text-amber-700">
                        {p.commission_code || '-'}
                      </span>
                    </TableCell>
                    <TableCell>
                      <span className="flex items-center gap-1.5 text-sm">
                        <Users className="w-4 h-4 text-slate-400" /> {p.member_count}
                      </span>
                    </TableCell>
                    <TableCell>
                      <span className="flex items-center gap-1.5 text-sm">
                        <UsersRound className="w-4 h-4 text-teal-400" /> {p.team_count}
                      </span>
                    </TableCell>
                    <TableCell>
                      <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold ${p.active ? 'bg-emerald-50 text-emerald-700' : 'bg-slate-100 text-slate-500'}`}>
                        {p.active ? 'Ativo' : 'Inativo'}
                      </span>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1">
                        <Button variant="ghost" size="icon" className="h-8 w-8" onClick={(e) => openEdit(e, p)} data-testid={`edit-partner-${p.id}`}>
                          <Edit className="w-4 h-4" />
                        </Button>
                        <Button variant="ghost" size="icon" className="h-8 w-8 text-red-500" onClick={(e) => handleDelete(e, p.id)} data-testid={`delete-partner-${p.id}`}>
                          <Trash2 className="w-4 h-4" />
                        </Button>
                        <ChevronRight className="w-4 h-4 text-slate-300" />
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
          )}
        </Card>
      </div>

      <Dialog open={showModal} onOpenChange={setShowModal}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>{editing ? 'Editar Parceiro' : 'Novo Parceiro'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label>Nome *</Label>
              <Input value={form.name} onChange={(e) => setForm(p => ({ ...p, name: e.target.value }))} className="mt-1 h-11 rounded-lg" data-testid="partner-name-input" />
            </div>
            <div>
              <Label>Parceiro Pai</Label>
              <Select value={form.parent_partner_id || 'none'} onValueChange={(v) => setForm(p => ({ ...p, parent_partner_id: v === 'none' ? '' : v }))}>
                <SelectTrigger className="mt-1 h-11 rounded-lg" data-testid="parent-partner-select">
                  <SelectValue placeholder="Nenhum (parceiro principal)" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Nenhum (parceiro principal)</SelectItem>
                  {partners.filter(p => p.id !== editing?.id).map(p => (
                    <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Codigo Comissao</Label>
              <Input value={form.commission_code} onChange={(e) => setForm(p => ({ ...p, commission_code: e.target.value }))} className="mt-1 h-11 rounded-lg" data-testid="partner-commission-input" placeholder="Ex: COM01" />
            </div>
            <div>
              <Label>Descricao</Label>
              <Input value={form.description} onChange={(e) => setForm(p => ({ ...p, description: e.target.value }))} className="mt-1 h-11 rounded-lg" data-testid="partner-description-input" />
            </div>

            {!editing && (
              <div className="border-t pt-4 mt-2">
                <label className="flex items-center gap-2 cursor-pointer select-none">
                  <input
                    type="checkbox"
                    checked={form.create_admin}
                    onChange={(e) => setForm(p => ({ ...p, create_admin: e.target.checked }))}
                    className="w-4 h-4 accent-[#2D3E6F]"
                    data-testid="partner-create-admin-toggle"
                  />
                  <span className="font-medium text-sm text-slate-800">Criar utilizador responsável (admin) deste parceiro</span>
                </label>
                {form.create_admin && (
                  <div className="space-y-3 mt-3 pl-2 border-l-2 border-[#4DD4E8]/40">
                    <p className="text-xs text-slate-500">Será criado um utilizador com role <strong>admin</strong> associado a este parceiro. Pode gerir os contratos, equipas e dashboards do seu parceiro.</p>
                    <div>
                      <Label className="text-xs">Nome do admin *</Label>
                      <Input
                        value={form.admin_name}
                        onChange={(e) => setForm(p => ({ ...p, admin_name: e.target.value }))}
                        className="mt-1 h-10 rounded-lg"
                        placeholder="Ex: Maria Sousa"
                        data-testid="partner-admin-name"
                      />
                    </div>
                    <div>
                      <Label className="text-xs">Email *</Label>
                      <Input
                        type="email"
                        value={form.admin_email}
                        onChange={(e) => setForm(p => ({ ...p, admin_email: e.target.value }))}
                        className="mt-1 h-10 rounded-lg"
                        placeholder="responsavel@parceiro.pt"
                        data-testid="partner-admin-email"
                      />
                    </div>
                    <div>
                      <Label className="text-xs">Password (min. 6 chars) *</Label>
                      <Input
                        type="password"
                        value={form.admin_password}
                        onChange={(e) => setForm(p => ({ ...p, admin_password: e.target.value }))}
                        className="mt-1 h-10 rounded-lg"
                        placeholder="••••••"
                        data-testid="partner-admin-password"
                      />
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowModal(false)} className="rounded-full">Cancelar</Button>
            <Button onClick={handleSave} disabled={saving} className="bg-[#2D3E6F] hover:bg-[#1a2a4a] rounded-full gap-2" data-testid="save-partner-btn">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
              {editing ? 'Atualizar' : 'Criar'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Layout>
  );
};

export default PartnersPage;
